import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";

import { IconChevronRight } from "@tabler/icons-react";
import * as LucideIcons from "lucide-react";

import React, { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "../ui/button";
import { useTabStore } from "@/stores/use-tab-store";
import { cn, devError } from "@/lib/utils";
import { Skeleton } from "../ui/skeleton";
import { Icons } from "../ui/icons";
import { MenuSearchBar } from "./menu-search-bar";
import { menuAPI } from "@/api/menu";
import { useI18n } from "@/contexts/I18nContext";
import { SidebarOpenButton } from "./sidebar-open-button";

export default function AppSidebar(props) {
  const { language, t } = useI18n();
  const { state: sidebarState, setOpen: setSidebarOpen } = useSidebar();
  const [search, setSearch] = useState("");
  const { addTab, activeTab } = useTabStore();
  const initializedRef = useRef(false);
  const [navItems, setNavItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const mapMenuData = (menuData) => {
    return menuData.map((menu) => ({
      title: t(`menu.${menu.menuId}`, menu.menuName),
      menuId: menu.menuId,
      url: menu.menuPath,
      icon: menu.menuIcon,
      type: menu.menuType,
      default: menu.default,
      isDefault: menu.default ? "Y" : "N",
      isActive: true,
      items:
        menu.children?.map((child) => ({
          title: t(`menu.${child.menuId}`, child.menuName),
          menuId: child.menuId,
          url: child.menuPath,
          icon: child.menuIcon,
          type: child.menuType,
          default: menu.default,
          isDefault: child.default ? "Y" : "N",
        })) || [],
    }));
  };

  const fetchMenus = async () => {
    try {
      setLoading(true);
      const data = await menuAPI.getMyMenus({ showGlobalLoading: false });
      const mappedData = mapMenuData(data.data || []);
      setNavItems(mappedData);
    } catch (error) {
      devError("메뉴 로딩 실패:", error);
      setNavItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, [language]);

  const filteredNavItems = useMemo(() => {
    if (!search || !navItems) return navItems;

    return navItems.filter((item) => {
      const titleMatch = item.title
        .toLowerCase()
        .includes(search.toLowerCase());
      const subItemsMatch = item.items?.some((subItem) =>
        subItem.title.toLowerCase().includes(search.toLowerCase())
      );
      return titleMatch || subItemsMatch;
    });
  }, [search, navItems]);

  useEffect(() => {
    if (!initializedRef.current && navItems && !loading && !activeTab) {
      const availableItems = navItems
        .flatMap((item) => {
          const subItems = item.items ?? [];
          const allItems = item.url
            ? [
                {
                  title: item.title,
                  url: item.url,
                  icon: item.icon,
                  type: item.type,
                  menuId: item.menuId,
                  isDefault: item.default ? "Y" : "N",
                },
                ...subItems,
              ]
            : subItems;
          return allItems;
        })
        .filter((menu) => Boolean(menu.url));

      const defaultItem =
        availableItems.find((menu) => menu.isDefault === "Y") ??
        availableItems[0];

      if (defaultItem) {
        addTab({
          id: defaultItem.url,
          title: defaultItem.title,
          url: defaultItem.url,
          type: defaultItem.type,
          menuId: defaultItem.menuId,
        });
        initializedRef.current = true;
      }
    }
  }, [navItems, loading, activeTab, addTab]);

  return (
    <Sidebar
      collapsible="icon"
      className="top-(--header-height) h-[calc(100svh-var(--header-height))]!"
      {...props}
    >
      <SidebarHeader className="gap-1">
        <div className="flex justify-end group-data-[collapsible=icon]:justify-center">
          <SidebarOpenButton />
        </div>
        <div className="group-data-[collapsible=icon]:hidden">
          <MenuSearchBar
            search={search}
            setSearch={setSearch}
            onRefresh={fetchMenus}
          />
        </div>
      </SidebarHeader>

      <SidebarContent className="overflow-x-hidden">
        <SidebarGroup>
          <SidebarMenu>
            {loading
              ? Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="px-2 py-1">
                    <Skeleton className="h-8 w-full rounded-md" />
                  </div>
                ))
              : filteredNavItems &&
                filteredNavItems.map((item) => {
                  const Icon =
                    Icons[item.icon] || LucideIcons[item.icon] || Icons.logo;

                  return item.items && item.items.length > 0 ? (
                    <Collapsible
                      key={item.title}
                      asChild
                      defaultOpen={item.isActive}
                      className="group/collapsible"
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton
                            tooltip={item.title}
                            onClick={() => {
                              if (sidebarState === "collapsed") {
                                setSidebarOpen(true);
                              }
                            }}
                          >
                            {Icon && <Icon />}
                            <span>{item.title}</span>
                            <IconChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>

                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {item.items.map((subItem) => {
                              const SubIcon =
                                subItem.icon &&
                                (Icons[subItem.icon] ||
                                  LucideIcons[subItem.icon])
                                  ? Icons[subItem.icon] ||
                                    LucideIcons[subItem.icon]
                                  : Icons.logo;
                              return (
                                <SidebarMenuSubItem key={subItem.title}>
                                  <SidebarMenuSubButton
                                    asChild
                                    isActive={activeTab === subItem.url}
                                    className={cn(
                                      "rounded-md transition-colors",
                                      "hover:bg-muted hover:text-foreground px-2 py-1",
                                      "data-[active=true]:bg-primary/10",
                                      "data-[active=true]:text-primary",
                                      "data-[active=true]:font-semibold",
                                      "data-[active=true]:border-primary data-[active=true]:border-l-2"
                                    )}
                                  >
                                    <Button
                                      variant="ghost"
                                      onClick={() =>
                                        addTab({
                                          id: subItem.url,
                                          title: subItem.title,
                                          url: subItem.url,
                                          type: subItem.type,
                                          menuId: subItem.menuId,
                                        })
                                      }
                                      className="flex w-full justify-start gap-2"
                                    >
                                      {SubIcon && <SubIcon />}
                                      <span>{subItem.title}</span>
                                    </Button>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  ) : (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        tooltip={item.title}
                        isActive={activeTab === item.url}
                      >
                        <Button
                          variant="ghost"
                          onClick={() =>
                            addTab({
                              id: item.url,
                              title: item.title,
                              url: item.url,
                              type: item.type,
                              menuId: item.menuId,
                            })
                          }
                          className="flex w-full justify-start gap-2"
                        >
                          <Icon />
                          <span>{item.title}</span>
                        </Button>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter />
      <SidebarRail />
    </Sidebar>
  );
}
